import { COUNTRIES } from "@/constants/util.constants";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn/components/ui/select";
import {
  Autocomplete,
  GoogleMap,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";

// Patch Radix Dialog's click outside logic for Google Autocomplete globally, so it persists across component mounts
let _radixPatched = false;
if (typeof window !== "undefined" && !_radixPatched) {
  _radixPatched = true;
  const eventTypes = ["pointerdown", "mousedown", "mouseup", "click"];
  const stopEvent = (e: Event) => {
    if ((e.target as HTMLElement).closest(".pac-container")) {
      e.stopPropagation();
    }
  };
  eventTypes.forEach((type) => {
    document.addEventListener(type, stopEvent, true);
  });
  // Inject a global style to force .pac-container to be interactive and above overlays
  const style = document.createElement("style");
  style.innerHTML = `
    .pac-container {
      pointer-events: auto !important;
      z-index: 9999 !important;
      position: fixed !important;
    }
    .pac-container * {
      pointer-events: auto !important;
    }
  `;
  document.head.appendChild(style);
  // No cleanup: this is intentional so the patch persists for the app lifetime
}

// Accept the full address object and a callback for changes
type Address = {
  addressLine1: string;
  addressLine2?: string;
  officeParkName?: string;
  suburb?: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  latitude: number;
  longitude: number;
};

type MapPickerProps = {
  activeBranchID: string;
  address: Address;
  onAddressChange: (address: Address) => void;
};

const MapPicker = ({
  address,
  onAddressChange,
  activeBranchID,
}: MapPickerProps) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Local state for address fields
  const [fields, setFields] = useState<Address>(address);

  const [center, setCenter] = useState<{ lat: number; lng: number }>({
    lat: address.latitude,
    lng: address.longitude,
  });
  const [markerPos, setMarkerPos] = useState<{ lat: number; lng: number }>({
    lat: address.latitude,
    lng: address.longitude,
  });

  // Ref for the Autocomplete instance
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  // Update local and parent state when a field changes
  const handleFieldChange = (field: keyof Address, value: string) => {
    const updated = { ...fields, [field]: value };
    setFields(updated);
    onAddressChange(updated);

    // If addressLine1 is changed, trigger autocomplete search
  };

  // When marker is dragged, reverse geocode and update fields
  const handleMarkerDrag = (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPos({ lat, lng });
    setCenter({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results[0]) {
        const parsed = parseAddress(results[0]);
        const updated = {
          ...fields,
          ...parsed,
          latitude: lat,
          longitude: lng,
        };
        setFields(updated);
        onAddressChange(updated);
      }
    });
  };

  // Parse Google address components into our Address shape
  const parseAddress = (
    place: google.maps.places.PlaceResult | google.maps.GeocoderResult
  ) => {
    // Use street_number and route for addressLine1 for accuracy, not the full formatted address
    const get = (type: string) =>
      place.address_components?.find((ac) => ac.types.includes(type))
        ?.long_name || "";
    const streetNumber = get("street_number");
    const route = get("route");
    return {
      // Use street number + route for addressLine1 to avoid using the entire formatted address
      addressLine1: [streetNumber, route].filter(Boolean).join(" "),
      addressLine2: "",
      officeParkName: "",
      suburb: get("sublocality") || get("sublocality_level_1"),
      city: get("locality"),
      province: get("administrative_area_level_1"),
      country: get("country"),
      postalCode: get("postal_code"),
    };
  };

  useEffect(() => {
    setFields({
      ...address,
      latitude: address.latitude || 0,
      longitude: address.longitude || 0,
    });

    setCenter({
      lat: address.latitude || 0,
      lng: address.longitude || 0,
    });
    setMarkerPos({
      lat: address.latitude || 0,
      lng: address.longitude || 0,
    });
    // No imperative autocomplete setup here, as PlaceAutocompleteElement is used in the DOM
  }, [address]);

  // Handler for PlaceAutocompleteElement selection (manual typing)
  const handlePlaceAutocomplete = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFieldChange("addressLine1", e.target.value);
  };

  // Handler for when a place is selected from the Autocomplete component
  const handlePlaceChanged = () => {
    if (!autocompleteRef.current) return;
    const place = autocompleteRef.current.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setCenter({ lat, lng });
    setMarkerPos({ lat, lng });

    // Parse address components from Google PlaceResult
    const parsed = parseAddress(place);
    const updated = {
      ...fields,
      ...parsed,
      latitude: lat,
      longitude: lng,
    };
    setFields(updated);
    onAddressChange(updated);
  };

  if (!isLoaded)
    return (
      <div className="py-4">
        <div className="flex items-center justify-center">
          <div className="h-10 w-10 animate-spin rounded-full border-b-3 border-white"></div>
        </div>
      </div>
    );

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`address-line-1-${activeBranchID}`}>
            Address Line 1 <span className="text-red-500">*</span>
          </Label>
          {/* Use the Autocomplete component from @react-google-maps/api */}
          <Autocomplete
            onLoad={(ac) => {
              autocompleteRef.current = ac;
            }}
            onPlaceChanged={handlePlaceChanged}
          >
            <Input
              id={`address-line-1-${activeBranchID}`}
              value={fields.addressLine1}
              placeholder="Enter address line 1"
              onChange={(e) =>
                handleFieldChange("addressLine1", e.target.value)
              }
            />
          </Autocomplete>
        </div>
        <div className="space-y-2">
          <div className="space-y-2">
            <Label htmlFor={`address-line-2-${activeBranchID}`}>
              Address Line 2
            </Label>
            <Input
              id={`address-line-2-${activeBranchID}`}
              value={fields.addressLine2 || ""}
              onChange={(e) =>
                handleFieldChange("addressLine2", e.target.value)
              }
              placeholder="Enter address line 2"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`office-park-name-${activeBranchID}`}>
            Office Park Name
          </Label>
          <Input
            id={`office-park-name-${activeBranchID}`}
            value={fields.officeParkName || ""}
            onChange={(e) =>
              handleFieldChange("officeParkName", e.target.value)
            }
            placeholder="Enter office park name"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`suburb-${activeBranchID}`}>
            Suburb <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`suburb-${activeBranchID}`}
            value={fields.suburb || ""}
            onChange={(e) => handleFieldChange("suburb", e.target.value)}
            placeholder="Enter suburb"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`city-${activeBranchID}`}>
            City <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`city-${activeBranchID}`}
            value={fields.city || ""}
            onChange={(e) => handleFieldChange("city", e.target.value)}
            placeholder="Enter city"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`province-${activeBranchID}`}>
            Province/State <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`province-${activeBranchID}`}
            value={fields.province || ""}
            onChange={(e) => handleFieldChange("province", e.target.value)}
            placeholder="Enter province"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`country-${activeBranchID}`}>
            Country <span className="text-red-500">*</span>
          </Label>
          <Select
            value={fields.country || ""}
            onValueChange={(value) => handleFieldChange("country", value)}
          >
            <SelectTrigger className="w-full max-w-full truncate">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((country) => (
                <SelectItem key={country.name} value={country.name}>
                  {country.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`postal-code-${activeBranchID}`}>
            Postal Code <span className="text-red-500">*</span>
          </Label>
          <Input
            id={`postal-code-${activeBranchID}`}
            value={fields.postalCode || ""}
            onChange={(e) => handleFieldChange("postalCode", e.target.value)}
            placeholder="Enter postal code"
          />
        </div>
      </div>
      <div className="mt-4">
        <p className="text-muted-foreground text-sm text-balance">
          Drag the pin to select your location. This location will be used for
          deliveries
        </p>
      </div>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "300px" }}
        center={center}
        zoom={16}
        mapContainerClassName="rounded-lg"
      >
        <Marker
          position={markerPos}
          draggable={true}
          onDragEnd={handleMarkerDrag}
        />
      </GoogleMap>
    </div>
  );
};

export default MapPicker;
